import { Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  ensureIsPolicyCategory,
  getTestSuiteScenarioTargetingInfo,
  type TestSuiteFormValues
} from 'components/test-suites/forms/test-suite-form-utils'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import { ReliabilityGuide } from 'components/test-suites/ReliabilityGuide'
import { type PolicyCategory } from 'types/api/policies/policy'

export const TestSuiteUnderstandingScoreCalculation: React.FC = () => {
  const { queryResultTestSuitesDetectedRisks, scenariosLookup } =
    useTestSuiteFormContext()
  const { data: detectedRisks } = queryResultTestSuitesDetectedRisks

  const { control } = useFormContext<TestSuiteFormValues>()

  const [testSuiteScenarios, excludedRiskIds] = useWatch({
    control,
    name: ['scenarios', 'excludedRiskIds']
  })

  const { categoriesFailureFlags, categoriesInfra } = useMemo(() => {
    const categoriesInfra = new Set<PolicyCategory>()
    const categoriesFailureFlags = new Set<PolicyCategory>()

    testSuiteScenarios.forEach((testSuiteScenario) => {
      const category = ensureIsPolicyCategory(testSuiteScenario.policyCategory)
      const scenario = scenariosLookup.get(testSuiteScenario.scenarioId)

      const target = scenario
        ? getTestSuiteScenarioTargetingInfo(scenario)
        : undefined

      if (!target || !(target.hasFailureFlagNode || target.hasInfraNode)) {
        return
      }

      if (target.hasInfraNode) {
        categoriesInfra.add(category)
      } else {
        categoriesFailureFlags.add(category)
      }
    })

    const hasIncludedRisks = Boolean(
      detectedRisks &&
        excludedRiskIds &&
        detectedRisks.length !== excludedRiskIds.length
    )

    return {
      categoriesFailureFlags: hasIncludedRisks
        ? categoriesFailureFlags.add('RISKS')
        : categoriesFailureFlags,
      categoriesInfra: hasIncludedRisks
        ? categoriesInfra.add('RISKS')
        : categoriesInfra
    }
  }, [detectedRisks, excludedRiskIds, scenariosLookup, testSuiteScenarios])

  return (
    <Stack>
      <Typography variant="h6">Understanding Score Calculation</Typography>
      <Typography variant="body1">
        Your Reliability Score consists of up to five categories (Scalability,
        Redundancy, Dependencies, Risks and Other). Empty categories will be
        excluded from your Score. Scores will be calculated differently if your
        test impacts Failure Flags or Infrastructure.
      </Typography>
      {categoriesInfra.size > 0 && (
        <ReliabilityGuide
          policyCategories={categoriesInfra}
          title="Infrastructure Score"
        />
      )}
      {categoriesFailureFlags.size > 0 && (
        <ReliabilityGuide
          policyCategories={categoriesFailureFlags}
          title="Failure Flags Score"
        />
      )}
    </Stack>
  )
}
